import { useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Printer, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function QRCodeDialog({ open, onOpenChange, url, businessName }) {
    const wrapRef = useRef(null);

    const downloadSVG = () => {
        const svg = wrapRef.current?.querySelector("svg");
        if (!svg) return;
        const source = new XMLSerializer().serializeToString(svg);
        const blob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = `qr-${(businessName || "business").replace(/\s+/g, "-").toLowerCase()}.svg`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
        toast.success("QR code downloaded");
    };

    const downloadPNG = () => {
        const svg = wrapRef.current?.querySelector("svg");
        if (!svg) return;
        const source = new XMLSerializer().serializeToString(svg);
        const svgBlob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
        const svgUrl = URL.createObjectURL(svgBlob);
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            const scale = 4;
            canvas.width = img.width * scale;
            canvas.height = img.height * scale;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#FFFFFF";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `qr-${(businessName || "business").replace(/\s+/g, "-").toLowerCase()}.png`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(link.href);
                URL.revokeObjectURL(svgUrl);
                toast.success("QR code downloaded");
            }, "image/png");
        };
        img.src = svgUrl;
    };

    const printQR = () => {
        const svg = wrapRef.current?.querySelector("svg");
        if (!svg) return;
        const source = new XMLSerializer().serializeToString(svg);
        const w = window.open("", "_blank", "width=600,height=700");
        if (!w) {
            toast.error("Popup blocked");
            return;
        }
        w.document.write(`<!doctype html><html><head><title>${businessName || "QR"}</title>
            <style>body{font-family:system-ui,sans-serif;text-align:center;padding:40px;margin:0}
            h1{font-size:20px;margin:0 0 6px}p{margin:0 0 24px;color:#666;font-size:12px;word-break:break-all}
            .qr{display:inline-block;padding:16px;background:#fff}
            @media print{@page{margin:20mm}}</style></head><body>
            <h1>${businessName || ""}</h1><p>${url}</p><div class="qr">${source}</div>
            <script>window.onload=function(){setTimeout(function(){window.print();},250);}<\/script>
            </body></html>`);
        w.document.close();
    };

    const copyUrl = async () => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success("URL copied");
        } catch {
            toast.error("Could not copy URL");
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md bg-zinc-900 border-white/10 text-zinc-100">
                <DialogHeader>
                    <DialogTitle className="font-display">QR Code · {businessName}</DialogTitle>
                    <DialogDescription className="text-zinc-400 break-all">{url}</DialogDescription>
                </DialogHeader>
                <div ref={wrapRef} className="flex justify-center py-4">
                    <div className="p-4 rounded-xl bg-white">
                        <QRCodeSVG
                            value={url || ""}
                            size={240}
                            level="H"
                            includeMargin={false}
                            data-testid="qr-code-svg"
                        />
                    </div>
                </div>
                <div className="flex justify-center gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={copyUrl} className="border-white/10 text-zinc-200 bg-transparent hover:bg-white/5" data-testid="qr-copy-url">
                        <Copy className="h-3.5 w-3.5 me-1.5" />
                        Copy URL
                    </Button>
                    <a href={url} target="_blank" rel="noreferrer">
                        <Button variant="outline" size="sm" className="border-white/10 text-zinc-200 bg-transparent hover:bg-white/5" data-testid="qr-open-page">
                            <ExternalLink className="h-3.5 w-3.5 me-1.5" />
                            Open public page
                        </Button>
                    </a>
                </div>
                <DialogFooter className="sm:justify-center gap-2 flex-wrap">
                    <Button onClick={downloadPNG} className="bg-brand hover:bg-brand-hover text-white" data-testid="qr-download-png">
                        <Download className="h-4 w-4 me-1.5" />
                        Download PNG
                    </Button>
                    <Button onClick={downloadSVG} variant="outline" className="border-white/10 text-zinc-200 bg-transparent hover:bg-white/5" data-testid="qr-download-svg">
                        <Download className="h-4 w-4 me-1.5" />
                        SVG
                    </Button>
                    <Button onClick={printQR} variant="outline" className="border-white/10 text-zinc-200 bg-transparent hover:bg-white/5" data-testid="qr-print">
                        <Printer className="h-4 w-4 me-1.5" />
                        Print
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
